
import StickyNavigation from "@/components/layout/StickyNavigation";
import { useNavigate } from "react-router-dom";

import { useParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Shield, CheckCircle, Phone, Mail } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";

const productData = {
  "property-all-risks": {
    title: "Property All Risks Insurance",
    description: "Comprehensive coverage for your physical business assets including buildings, equipment, and inventory against a wide range of risks.",
    detailedDescription: "Property All Risks insurance provides extensive protection for your business premises and contents. Unlike basic fire and theft policies, this comprehensive coverage protects against a wide range of perils unless specifically excluded, giving you peace of mind that your valuable business assets are protected.",
    keyBenefits: [
      "Coverage for buildings, equipment, and inventory",
      "Protection against fire, theft, vandalism, and natural disasters",
      "Business interruption coverage options",
      "Temporary accommodation costs if premises become uninhabitable",
      "Professional reinstatement and replacement cost coverage",
      "Coverage for loss of rent and alternative accommodation"
    ],
    coverage: [
      "Building structure and fixtures",
      "Office equipment and machinery",
      "Stock and inventory",
      "Furniture and fittings",
      "Documents and records",
      "Money and valuable papers"
    ],
    faqs: [
      {
        question: "What is the difference between Property All Risks and basic property insurance?",
        answer: "Property All Risks covers a much broader range of perils and typically operates on an 'all risks' basis, meaning coverage applies unless specifically excluded. Basic property insurance usually only covers named perils like fire and theft."
      },
      {
        question: "Are natural disasters covered?",
        answer: "Most natural disasters are covered including storms, floods, earthquakes, and other weather-related damage, subject to policy terms and conditions."
      },
      {
        question: "How is the sum insured determined?",
        answer: "The sum insured should reflect the full replacement cost of your property and contents. We recommend regular valuations to ensure adequate coverage."
      },
      {
        question: "What about temporary business premises?",
        answer: "Many policies include coverage for temporary accommodation and additional costs if your premises become uninhabitable due to an insured event."
      }
    ]
  },
  "third-party-liability": {
    title: "Third Party Liability Insurance",
    description: "Essential protection against claims for bodily injury or property damage caused by your business operations to third parties.",
    detailedDescription: "Third Party Liability insurance is crucial for any business that interacts with customers, suppliers, or the general public. This coverage protects your business against claims for accidental injury to people or damage to property caused by your business activities.",
    keyBenefits: [
      "Protection against bodily injury claims",
      "Coverage for property damage to third parties",
      "Legal defense costs included",
      "Product liability coverage options",
      "Professional legal support",
      "Worldwide coverage options available"
    ],
    coverage: [
      "Bodily injury to third parties",
      "Property damage claims",
      "Legal defense and court costs",
      "Product liability claims",
      "Advertising injury claims",
      "Personal injury claims"
    ],
    faqs: [
      {
        question: "Who is considered a 'third party'?",
        answer: "A third party is anyone who is not an employee of your business, including customers, visitors, suppliers, contractors, and members of the general public."
      },
      {
        question: "Does this cover product defects?",
        answer: "Product liability coverage can be included to protect against claims arising from defective products that cause injury or damage."
      },
      {
        question: "What legal costs are covered?",
        answer: "The policy typically covers legal defense costs, court fees, and settlements or judgments up to the policy limit."
      },
      {
        question: "Is coverage available internationally?",
        answer: "Many policies offer worldwide coverage, but it's important to check the specific territorial limits of your policy."
      }
    ]
  },
  "workers-compensation": {
    title: "Workers Compensation Insurance",
    description: "Mandatory coverage in most jurisdictions for employee injuries or illnesses arising from workplace activities.",
    detailedDescription: "Workers Compensation insurance provides financial protection for both employers and employees. It covers medical expenses, lost wages, and rehabilitation costs for employees who suffer work-related injuries or illnesses, while protecting employers from potential lawsuits.",
    keyBenefits: [
      "Medical expense coverage for injured employees",
      "Lost wage compensation",
      "Rehabilitation and retraining costs",
      "Legal protection for employers",
      "Death benefits for dependents",
      "Return-to-work support programs"
    ],
    coverage: [
      "Medical treatment costs",
      "Temporary disability benefits",
      "Permanent disability benefits",
      "Vocational rehabilitation",
      "Death benefits",
      "Legal defense costs"
    ],
    faqs: [
      {
        question: "Is Workers Compensation insurance mandatory?",
        answer: "In most jurisdictions, Workers Compensation insurance is legally required for businesses with employees. Requirements vary by location and industry."
      },
      {
        question: "What injuries are covered?",
        answer: "Coverage includes injuries and illnesses that arise out of and in the course of employment, including accidents, occupational diseases, and repetitive strain injuries."
      },
      {
        question: "How are premiums calculated?",
        answer: "Premiums are typically based on your industry classification, payroll amounts, and claims history. Safer workplaces often receive better rates."
      },
      {
        question: "What if an employee is partially at fault?",
        answer: "Workers Compensation is a no-fault system, meaning employees are generally covered regardless of who caused the accident."
      }
    ]
  },
  "cyber-insurance": {
    title: "Cyber Insurance",
    description: "Essential protection against data breaches, cyberattacks, and digital liabilities in our increasingly connected business world.",
    detailedDescription: "As businesses become more digital, cyber threats continue to evolve and increase. Cyber insurance provides comprehensive protection against various cyber risks including data breaches, ransomware attacks, business email compromise, and other digital threats that could devastate your business.",
    keyBenefits: [
      "Data breach response and notification costs",
      "Ransomware and extortion coverage",
      "Business interruption from cyber events",
      "Regulatory fines and penalties coverage",
      "Cyber crime and fraud protection",
      "Reputation management support"
    ],
    coverage: [
      "Data breach costs and notifications",
      "Ransomware payments and recovery",
      "Business interruption losses",
      "Cyber extortion demands",
      "Regulatory investigation costs",
      "Credit monitoring for affected parties"
    ],
    faqs: [
      {
        question: "What constitutes a data breach?",
        answer: "A data breach occurs when sensitive, protected, or confidential data is accessed, disclosed, or stolen by unauthorized individuals."
      },
      {
        question: "Does this cover ransomware attacks?",
        answer: "Yes, most cyber insurance policies cover ransomware attacks including the ransom payment (where legally permitted), data recovery costs, and business interruption."
      },
      {
        question: "Are regulatory fines covered?",
        answer: "Many policies include coverage for regulatory fines and penalties imposed due to data protection violations, subject to local laws."
      },
      {
        question: "What about social engineering fraud?",
        answer: "Advanced policies often cover social engineering attacks where employees are tricked into transferring funds or revealing sensitive information."
      }
    ]
  },
  "fleet-insurance": {
    title: "Fleet Insurance",
    description: "Comprehensive coverage for your business vehicles including cars, trucks, vans, and specialized commercial vehicles.",
    detailedDescription: "Fleet insurance provides comprehensive coverage for businesses operating multiple vehicles. Whether you have a small fleet of delivery vans or a large transport operation, fleet insurance offers cost-effective protection with centralized policy management.",
    keyBenefits: [
      "Coverage for multiple vehicles under one policy",
      "Competitive group pricing",
      "Flexible coverage options",
      "Driver protection and training programs",
      "24/7 claims support and roadside assistance",
      "Fleet management tools and reporting"
    ],
    coverage: [
      "Vehicle damage and theft",
      "Third party liability",
      "Driver personal accident cover",
      "Goods in transit protection",
      "Breakdown and recovery",
      "Replacement vehicle costs"
    ],
    faqs: [
      {
        question: "How many vehicles qualify for fleet insurance?",
        answer: "Most insurers require a minimum of 5 vehicles, but some may offer fleet policies for as few as 2-3 vehicles."
      },
      {
        question: "Can I add and remove vehicles during the policy term?",
        answer: "Yes, fleet policies typically offer flexibility to add or remove vehicles with pro-rata premium adjustments."
      },
      {
        question: "What driver requirements apply?",
        answer: "Drivers usually need to meet age and license requirements. Some policies include driver training programs to improve safety and reduce premiums."
      },
      {
        question: "Is international coverage available?",
        answer: "Many fleet policies can include coverage for international travel, particularly useful for businesses operating across borders."
      }
    ]
  },
  "professional-indemnity": {
    title: "Professional Indemnity Insurance",
    description: "Essential coverage for businesses providing professional advice or services, protecting against claims of negligence or errors.",
    detailedDescription: "Professional Indemnity insurance protects businesses and professionals who provide advice, consultation, or professional services. This coverage is essential for consultants, advisors, and service providers who could face claims alleging professional negligence, errors, or omissions.",
    keyBenefits: [
      "Protection against negligence claims",
      "Coverage for errors and omissions",
      "Legal defense cost coverage",
      "Retroactive coverage for past work",
      "Dishonesty of employees coverage",
      "Regulatory investigation support"
    ],
    coverage: [
      "Professional negligence claims",
      "Errors and omissions in services",
      "Breach of professional duty",
      "Legal defense and court costs",
      "Loss of documents or data",
      "Dishonest acts by employees"
    ],
    faqs: [
      {
        question: "Who needs Professional Indemnity insurance?",
        answer: "Any business providing professional advice, consultation, or specialized services should consider this coverage. It's often required by professional bodies and clients."
      },
      {
        question: "What is retroactive coverage?",
        answer: "Retroactive coverage protects against claims arising from work performed before the policy start date, up to a specified retroactive date."
      },
      {
        question: "How long after completing work am I covered?",
        answer: "Claims can be made years after work is completed. The policy covers claims made during the policy period, regardless of when the work was performed."
      },
      {
        question: "Does this cover subcontractors?",
        answer: "Coverage can often be extended to include subcontractors working on your behalf, subject to policy terms and conditions."
      }
    ]
  },
  "business-interruption": {
    title: "Business Interruption Insurance",
    description: "Financial protection for lost income and ongoing expenses when your business operations are disrupted by covered events.",
    detailedDescription: "Business Interruption insurance helps maintain your business's financial stability when operations are disrupted by covered events such as fires, natural disasters, or other insured perils. This coverage helps replace lost income and covers ongoing expenses during the recovery period.",
    keyBenefits: [
      "Lost revenue replacement during downtime",
      "Coverage for ongoing business expenses",
      "Additional expense coverage for temporary operations",
      "Loss of rental income protection",
      "Extended coverage for supply chain disruptions",
      "Professional business interruption assessment"
    ],
    coverage: [
      "Lost gross profit and revenue",
      "Ongoing business expenses",
      "Additional operating expenses",
      "Loss of rental income",
      "Extra expenses for temporary premises",
      "Professional fees for claims preparation"
    ],
    faqs: [
      {
        question: "When does Business Interruption coverage apply?",
        answer: "Coverage applies when your business operations are interrupted by an insured peril that causes physical damage to your property."
      },
      {
        question: "How is the indemnity period determined?",
        answer: "The indemnity period is the time needed to restore your business to its pre-loss condition, subject to the maximum period specified in your policy."
      },
      {
        question: "Are supply chain disruptions covered?",
        answer: "Extended coverage can include interruptions caused by damage to suppliers or customers' premises, subject to specific terms and sub-limits."
      },
      {
        question: "How is lost profit calculated?",
        answer: "Lost profit is typically calculated based on your historical financial performance and projected business performance, adjusted for market trends."
      }
    ]
  }
};

const ProductDetail = () => {
  const { productId } = useParams<{ productId: string }>();
  const { tenant } = useTenant();

  if (!productId || !productData[productId as keyof typeof productData]) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">Product Not Found</h1>
          <Link to="/" className="text-blue-600 hover:text-blue-700">
            Return to Home
          </Link>
        </div>
      </div>
    );
  }

  const product = productData[productId as keyof typeof productData];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      <StickyNavigation />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Home
          </Link>
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 bg-blue-500 rounded-lg flex items-center justify-center">
              <Shield className="h-8 w-8 text-white" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900">{product.title}</h1>
              <p className="text-xl text-gray-600">{product.description}</p>
            </div>
          </div>
        </div>

        {/* Quick Quote CTA */}
        <Card className="mb-8 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row items-center justify-between">
              <div className="mb-4 md:mb-0">
                <h3 className="text-xl font-semibold text-gray-900 mb-2">Ready to Get Protected?</h3>
                <p className="text-gray-600">Get a personalized quote in minutes</p>
              </div>
              <div className="flex gap-3">
                <Button size="lg" style={{ backgroundColor: tenant?.colorScheme?.startMatrixButtonColor || undefined, color: tenant?.colorScheme?.startMatrixButtonColor ? '#FFFFFF' : undefined }}>Get Quote Now</Button>
                <Button variant="outline" size="lg">
                  <Phone className="h-4 w-4 mr-2" />
                  Call Us
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Detailed Description */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>About {product.title}</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-700 leading-relaxed">{product.detailedDescription}</p>
          </CardContent>
        </Card>

        {/* Key Benefits */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Key Benefits</CardTitle>
            <CardDescription>Why choose {product.title} for your business</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {product.keyBenefits.map((benefit, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{benefit}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Coverage Details */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>What's Covered</CardTitle>
            <CardDescription>Comprehensive coverage details</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {product.coverage.map((item, index) => (
                <div key={index} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <Shield className="h-4 w-4 text-blue-600" />
                  <span className="text-gray-700">{item}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* FAQ Section */}
        <Card className="mb-8">
          <CardHeader>
            <CardTitle>Frequently Asked Questions</CardTitle>
            <CardDescription>Common questions about {product.title}</CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion type="single" collapsible className="w-full">
              {product.faqs.map((faq, index) => (
                <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-left">{faq.question}</AccordionTrigger>
                  <AccordionContent className="text-gray-600 leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </CardContent>
        </Card>

        {/* Contact CTA */}
        <Card className="bg-gray-900 text-white">
          <CardContent className="pt-6">
            <div className="text-center">
              <h3 className="text-2xl font-bold mb-4">Have More Questions?</h3>
              <p className="text-gray-300 mb-6">
                Our insurance experts are here to help you find the perfect coverage for your business.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  variant="secondary"
                  onClick={() => window.open('mailto:business@yallacompare.com', '_self')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Email Us
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  className="text-white border-white hover:bg-white hover:text-gray-900"
                  onClick={() => window.open('tel:+971586948688', '_self')}
                >
                  <Phone className="h-4 w-4 mr-2" />
                  Call Now
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProductDetail;
